import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";
import { useRef } from "react";
import { User } from "features/User/models";
import { useIsCurrentUserCompanyAdmin } from "hooks/useCurrentUserPermissions";
import { useDeactivateUser, useUserDetailQuery } from "features/User/hooks";

interface DeactivateUserConfirmProps {
  userId: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export function DeactivateUserConfirm({ userId, onClose, onSuccess }: DeactivateUserConfirmProps) {
  const isAdmin = useIsCurrentUserCompanyAdmin();
  const cancelRef = useRef(null);

  const { data: user } = useUserDetailQuery(userId as string, {
    enabled: !!userId && isAdmin,
  });

  const { mutate: deactivate } = useDeactivateUser(user as User, () => {
    onClose();
    onSuccess?.();
  });

  const handleConfirm = () => {
    if (user) {
      deactivate();
    }
  };

  if (!isAdmin || !userId || !user || user.deactivatedAt) return null;

  return (
    <AlertDialog
      isOpen={!!userId}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Confirm User Deactivation
          </AlertDialogHeader>

          <AlertDialogBody>
            This cannot be undone! Be sure you want to deactivate this user (they no longer work at your company).
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleConfirm} ml={3}>
              Deactivate User
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
